import {
  AppstoreOutlined,
  BookOutlined,
  EditOutlined,
  FireOutlined,
  GiftOutlined,
  InboxOutlined,
  LinkOutlined,
  MessageOutlined,
  RobotOutlined,
  SafetyCertificateOutlined,
  SaveOutlined,
  SettingOutlined,
  UserOutlined,
} from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history, Link } from '@umijs/max';
import { Button } from 'antd';
import React from 'react';
import {
  AvatarDropdown,
  AvatarName,
  CookieConsent,
  Footer,
  MembershipExpiryReminder,
  ProductSwitcher,
  Question,
  SelectLang,
} from '@/components';
import { queryCurrentUserProfile, toCurrentUser } from '@/services/user';
import {
  clearAuthToken,
  saveAggregationBaseUrl,
  saveAuthUserId,
} from '@/utils/auth';
import {
  currentConsoleCtx,
  firstConsoleItemLabel,
  setConsoleCtx,
} from '@/utils/consoleCtx';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import '@ant-design/v5-patch-for-react-19';

// ---- Console sidebar menu definition ----
// Flat menu items for console context (no nesting — all at top level).
// Product context is persisted in sessionStorage via @/utils/consoleCtx.

/** Build console menu items filtered by product context */
function buildConsoleMenu(ctx: string): any[] {
  const items: any[] = [];

  // 顶部项：产品上下文内为「产品中心」入口（点击唤起产品切换抽屉，不跳回 /console）；
  // /console 首页（无产品上下文）同样为「产品中心」。文案走显式判断 firstConsoleItemLabel，
  // 不再用 `ctx ? '控制台' : '产品中心'` 这种隐式逻辑（WIS-532）。
  items.push({
    name: firstConsoleItemLabel(ctx),
    path: '/console',
    icon: <AppstoreOutlined />,
  });

  // --- Content Incubator sub-navigation ---
  if (ctx === 'incubator') {
    // 一、爆款复刻中心
    // 注意：父级 path 必须与所有二级 path 不同，否则 ProLayout 打平后父子共用同一 key，
    // 面包屑回溯父链时会命中子项自身，导致只渲染一级（WIS-369）。各父级走独立 section 重定向。
    items.push({
      name: '爆款复刻中心',
      icon: <FireOutlined />,
      path: '/content-workstation/reference',
      routes: [
        {
          name: '图文爆款复刻',
          path: '/content-workstation/reference-copy',
        },
        {
          name: '视频爆款复刻',
          path: '/content-workstation/video-reference-copy',
        },
      ],
    });
    // 二、爆款内容工坊（WIS-369 opt2：文案创作、视频制作暂时不开放，隐藏导航与页面，仅留图片创作）
    items.push({
      name: '爆款内容工坊',
      icon: <EditOutlined />,
      path: '/content-workstation/workshop',
      routes: [
        {
          name: '图片创作',
          path: '/content-workstation/image-generation',
        },
      ],
    });
    // 三、素材资产中心（WIS-369：与「爆款内容工坊」对调顺序）
    items.push({
      name: '素材资产中心',
      icon: <InboxOutlined />,
      path: '/content-workstation/material',
      routes: [
        {
          name: '素材采集',
          path: '/content-workstation/material/collect',
        },
        {
          name: '素材资产管理',
          path: '/content-workstation/material/assets',
        },
      ],
    });
    // 四、稿件箱（一级，无二级页面）
    items.push({
      name: '稿件箱',
      icon: <SaveOutlined />,
      path: '/content-workstation/drafts-library',
    });
    // 五、知识库（一级，无二级页面）
    items.push({
      name: '知识库',
      icon: <BookOutlined />,
      path: '/content-workstation/knowledge',
    });
    // 六、创作规则（一级，无二级页面）
    items.push({
      name: '创作规则',
      icon: <SettingOutlined />,
      path: '/content-workstation/settings',
    });
  }

  // --- AI客服 sub-navigation ---
  if (ctx === 'ai-service') {
    items.push(
      { name: 'AI员工', path: '/ai-employee', icon: <RobotOutlined /> },
      {
        name: '客服管理',
        icon: <MessageOutlined />,
        path: '/customer-service',
        routes: [
          { name: '消息聚合', path: '/customer-service/message-aggregation' },
          { name: '历史消息', path: '/customer-service/history' },
        ],
      },
      {
        name: '话术与知识库',
        icon: <BookOutlined />,
        path: '/knowledge',
        routes: [
          { name: '关键词模板', path: '/knowledge/keywords' },
          { name: '问答模板', path: '/knowledge/qa' },
          { name: '综合知识库', path: '/knowledge/comprehensive' },
        ],
      },
    );
  }

  // 积分与成本、系统设置 — 仅在 console 首页显示（无产品上下文时）
  if (!ctx) {
    items.push({
      name: '积分与成本',
      path: '/token-management',
      icon: <GiftOutlined />,
    });
    items.push({
      name: '系统设置',
      icon: <SettingOutlined />,
      path: '/account-config',
      routes: [
        {
          name: '账户设置',
          path: '/account-config/info',
          icon: <UserOutlined />,
        },
        {
          name: '企业认证',
          path: '/account-config/enterprise',
          icon: <SafetyCertificateOutlined />,
        },
        {
          name: '关联账号管理',
          path: '/account-config/linked-accounts',
          icon: <LinkOutlined />,
        },
      ],
    });
  }

  return items;
}

/** Paths that always show console sidebar (regardless of product context) */
const CONSOLE_BASE_PATHS = [
  '/console',
  '/customization-workstation',
  '/account-config',
  '/token-management',
];

/** Paths that show console sidebar only when a product context is active */
const PRODUCT_CONTEXT_PATHS = [
  '/customer-service',
  '/knowledge',
  '/ai-employee',
  '/content-workstation',
  '/advanced-tools',
];

function isConsolePath(pathname: string): boolean {
  // Base console paths always trigger console sidebar
  if (
    CONSOLE_BASE_PATHS.some(
      (p) => pathname === p || pathname.startsWith(`${p}/`),
    )
  ) {
    return true;
  }
  // Product sub-paths only show console sidebar when a product context is active。
  // WIS-369：ctx 既可来自 sessionStorage，也可由路径推导（currentConsoleCtx），
  // 保证直接访问/书签打开内容孵化器等页面也能落到正确的产品侧栏。
  if (
    currentConsoleCtx(pathname) &&
    PRODUCT_CONTEXT_PATHS.some(
      (p) => pathname === p || pathname.startsWith(`${p}/`),
    )
  ) {
    return true;
  }
  return false;
}

const isDev = process.env.NODE_ENV === 'development' || process.env.CI;
const loginPath = '/user/login';
const publicUserPaths = new Set([
  loginPath,
  '/user/forgot-password',
  '/user/register',
  '/user/register-result',
]);

const normalizePathname = (pathname?: string) => {
  const raw = String(pathname || '/').trim();
  if (!raw) return '/';
  const withoutQuery = raw.split('?')[0].split('#')[0];
  const normalized = withoutQuery.replace(/\/+$/, '');
  return normalized || '/';
};

const isPublicUserPath = (pathname?: string) =>
  publicUserPaths.has(normalizePathname(pathname));

/**
 * Detect SSO code handoff from official website.
 * Official website appends wis_code (one-time, 60s TTL) to URL when redirecting.
 * Call BFF inject endpoint to exchange code for httpOnly cookies, then clean URL.
 */
const injectSsoCode = async (): Promise<boolean> => {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  const code = params.get('wis_code');
  if (!code) return false;

  try {
    const resp = await fetch('/bff/sso/inject', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    });
    if (!resp.ok) return false;
    const data = await resp.json();
    if (!data.success) return false;

    // SSO 注入通过 httpOnly cookie 完成。清掉可能来自旧会话的浏览器存储凭证，
    // 避免 request interceptor 继续发送已过期 JWT，覆盖 BFF 刚写入的新 cookie。
    clearAuthToken();

    // Clean URL params without reload
    params.delete('wis_code');
    const clean = params.toString();
    const newPath =
      window.location.pathname +
      (clean ? `?${clean}` : '') +
      window.location.hash;
    window.history.replaceState(null, '', newPath);
    return true;
  } catch {
    return false;
  }
};

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
  /** 产品切换抽屉开关（侧栏首项「产品中心」点击时置位，见 menuItemRender） */
  productSwitcherOpen?: boolean;
}> {
  const fetchUserInfo = async () => {
    try {
      const profile = await queryCurrentUserProfile({
        skipErrorHandler: true,
      });
      const currentUser = toCurrentUser(profile);
      if (currentUser.userid) {
        saveAuthUserId(currentUser.userid);
      }
      saveAggregationBaseUrl(currentUser.aggregationHttpBaseUrl);
      return currentUser;
    } catch (_error) {
      clearAuthToken();
      history.push(loginPath);
    }
    return undefined;
  };
  // SSO code handoff: detect wis_code in URL, exchange for cookies
  await injectSsoCode();
  // 如果不是登录页面，执行
  const { location } = history;
  if (!isPublicUserPath(location.pathname)) {
    const currentUser = await fetchUserInfo();
    return {
      fetchUserInfo,
      currentUser,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// header logo 标题：按当前产品页拼后缀（覆盖默认静态 title "Wischoicer AI"）
const HEADER_TITLE_SUFFIX: Record<string, string> = {
  '/console': '控制台',
  // WIS-369 opt1：孵化器默认落点改为图文爆款复刻
  '/content-workstation/reference-copy': '内容孵化器',
};

// 内容孵化器面包屑映射（WIS-369）。
// 内容孵化器各页 URL 是扁平的，ProLayout 的面包屑依据 urlToList 按 URL 段拆分回溯，
// 无法自动还原侧栏「一级 / 二级」分组；故在 layout 级 breadcrumbRender 按侧栏一二级关系
// 集中纠正，非孵化器路径回退自动面包屑。
// 注：文案创作 /creation、视频制作 /video-production 已下线（WIS-369 opt2），不再列入。
const INCUBATOR_BREADCRUMBS: Record<
  string,
  { primary: string; secondary?: string }
> = {
  '/content-workstation/reference-copy': {
    primary: '爆款复刻中心',
    secondary: '图文爆款复刻',
  },
  '/content-workstation/video-reference-copy': {
    primary: '爆款复刻中心',
    secondary: '视频爆款复刻',
  },
  '/content-workstation/image-generation': {
    primary: '爆款内容工坊',
    secondary: '图片创作',
  },
  '/content-workstation/material/collect': {
    primary: '素材资产中心',
    secondary: '素材采集',
  },
  '/content-workstation/material/assets': {
    primary: '素材资产中心',
    secondary: '素材资产管理',
  },
  '/content-workstation/drafts-library': { primary: '稿件箱' },
  '/content-workstation/knowledge': { primary: '知识库' },
  '/content-workstation/settings': { primary: '创作规则' },
};

/** 内容孵化器面包屑：命中返回显式 items，未命中返回 undefined（沿用自动面包屑） */
function buildIncubatorBreadcrumbItems(pathname: string) {
  // 生产构建 pathname 可能带结尾斜杠，先归一化去掉 query/hash/结尾斜杠，保证精确映射命中。
  const p =
    String(pathname || '/')
      .replace(/\?.*$/, '')
      .replace(/#.*$/, '')
      .replace(/\/+$/, '') || '/';
  const exact = INCUBATOR_BREADCRUMBS[p];
  if (exact) {
    return [
      { title: exact.primary },
      ...(exact.secondary ? [{ title: exact.secondary }] : []),
    ];
  }
  return undefined;
}

const HeaderTitle: React.FC = () => {
  const suffix = HEADER_TITLE_SUFFIX[history.location.pathname];
  return <>{`Wischoicer AI${suffix ? `-${suffix}` : ''}`}</>;
};

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  return {
    title: <HeaderTitle />,
    actionsRender: () => [
      <Button
        key="console"
        type="text"
        icon={<AppstoreOutlined />}
        onClick={() => history.push('/console')}
      >
        控制台
      </Button>,
      <Question key="doc" />,
      <SelectLang key="SelectLang" />,
    ],
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    footerRender: () => <Footer />,
    // WIS-369：内容孵化器面包屑按侧栏一二级关系显式映射；其余路径沿用自动面包屑
    breadcrumbRender: (routers: any = []) => {
      const override = buildIncubatorBreadcrumbItems(history.location.pathname);
      return override ?? routers;
    },
    onPageChange: () => {
      const { location } = history;
      // 如果没有登录，重定向到 login
      if (!initialState?.currentUser && !isPublicUserPath(location.pathname)) {
        history.push(loginPath);
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: isDev
      ? [
          <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
            <LinkOutlined />
            <span>OpenAPI 文档</span>
          </Link>,
        ]
      : [],
    menuHeaderRender: undefined,
    menuDataRender: (menuData) => {
      const { location } = history;
      if (isConsolePath(location.pathname)) {
        // Clear context when user returns to product center
        if (location.pathname === '/console') {
          setConsoleCtx('');
        }
        // WIS-369：路径推导优先，直接访问/书签打开产品页也能拿到正确产品侧栏
        const ctx = currentConsoleCtx(location.pathname);
        return buildConsoleMenu(ctx);
      }
      return menuData;
    },
    menuItemRender: (item, defaultDom) => {
      const { location } = history;
      const menuItemProps = item as any;
      const itemPath = menuItemProps?.itemPath || menuItemProps?.path;

      // WIS-369：产品上下文内点击侧栏首项「产品中心」→ 唤起产品切换抽屉（不跳回 /console）。
      // ctx 走 currentConsoleCtx（路径推导），直接进入产品页时也能触发抽屉。
      if (itemPath === '/console' && currentConsoleCtx(location.pathname)) {
        return (
          <div
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setInitialState((prev) => ({
                ...prev,
                productSwitcherOpen: true,
              }));
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              width: '100%',
            }}
          >
            {defaultDom}
          </div>
        );
      }

      // ⚠️ 一旦自定义 menuItemRender，就会覆盖 @umijs/plugin-layout 默认的 menuItemRender
      // （@umijs/plugins/dist/layout.js），而默认实现正是把菜单项包进 <Link to={path}>
      // 实现跳转的。若直接 return defaultDom，所有非首项「产品中心」菜单项点击都不会跳转
      // （实测：几乎所有页面侧栏点击无反应）。这里按默认实现复刻 <Link> 包裹。
      if (menuItemProps.isUrl || menuItemProps.children) {
        return defaultDom;
      }
      if (menuItemProps.path && location.pathname !== menuItemProps.path) {
        return (
          <Link
            to={menuItemProps.path.replace('/*', '')}
            target={menuItemProps.target}
          >
            {defaultDom}
          </Link>
        );
      }
      return defaultDom;
    },
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          {children}
          <ProductSwitcher
            open={Boolean(initialState?.productSwitcherOpen)}
            onClose={() =>
              setInitialState((prev) => ({
                ...prev,
                productSwitcherOpen: false,
              }))
            }
          />
          <MembershipExpiryReminder />
          <CookieConsent />
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  // 使用同源请求，避免生产环境误发到默认示例域名
  baseURL: process.env.REACT_APP_REQUEST_BASE_URL || undefined,
  ...errorConfig,
};

// ─── Media-caching service worker ───
// Registers /sw-media.js (see public/sw-media.js) which caches OSS image
// responses keyed by path, defeating presigned URL rotation so reopening a
// task no longer re-downloads the same images. Image-only + best-effort;
// failure to register never blocks the app.
if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw-media.js').catch(() => {
      // best-effort: ignore registration failures
    });
  });
}
